/**
 * The career-chart check's form props, shared by the server-rendered shell
 * (CareerChartCheckLazy), the island's BirthDetailsForm and
 * BirthToolFormShell.parity.test.tsx, so every frame before and after the island
 * loads reads the same words. No name field: the answer does not use one. The
 * birth time is required: the 10th house and its lord are counted from the
 * Lagna, which moves into the next sign about every two hours.
 */
export const CAREER_CHART_CHECK_FORM = {
  language: "en",
  submitLabel: "See My Career Chart",
  collectName: false,
  requireBirthTime: true,
} as const;

/**
 * One box for every state of the check's slot (the love check's frame,
 * LOVE_CHART_CHECK_FRAME): the shell, the loading shell, the live form and the
 * answer each fill the slot's reserved height, and the submit sits at the foot
 * of the box in every state with one 48 px height.
 */
export const CAREER_CHART_CHECK_FRAME =
  "mx-auto flex w-full max-w-md flex-1 flex-col [&>form]:flex [&>form]:flex-1 [&>form]:flex-col [&>form>:last-child]:mt-auto [&>form>:last-child]:min-h-12";

/**
 * The form's room: the slot's floor (CareerChartCheckLazy) and, once the
 * answer's room is added, the form frame's own height, so the submit stays
 * where the press found it instead of sliding to the foot of the taller box.
 */
export const CAREER_CHART_FORM_RESERVE = "min-h-[520px] sm:min-h-[460px]";

/**
 * The answer's room, added by the island inside the submit itself, before the
 * request goes out (the love check's pattern): the growth happens in the press,
 * an input-driven change rather than a layout shift, and the reply, an error or
 * a retry all land inside it; only "Check another birth date" releases it.
 * Measured 2026-10-18 on next dev with POST /kundali mocked (Playwright, service
 * workers blocked, the check in the page's hero): a typical answer (one graha
 * in the 10th) and an empty 10th are 704 px at 320 wide, 686 at 360-390, 632 at
 * 414 and 564 from 480 up; five grahas in the 10th are 758 at 320, 704 at
 * 360-414 and 618 from 480; six grahas with the 10th lord carrying every flag
 * are 776 at 320, 722 at 360, 704 at 390-414 and 618 from 480. The tallest
 * answer at phone widths (trueWorstCareerReply: eight grahas in the 10th, the
 * most a chart can hold since Rahu and Ketu are always opposite; the 10th lord's
 * mahadasha the one running at birth, the longer period row; a September
 * antardasha end) is 830 at 320, 740 at 360-390, 722 at 414 and 600 from 480 up.
 * Hence 848 px on phones and 640 from the sm breakpoint; with it no answer, error
 * or retry moved the guide at any of seven widths (only input-driven shifts). A
 * static reserve this tall would leave ~300 px of blank under the form for every
 * reader who never checks a chart.
 */
export const CAREER_CHART_ANSWER_RESERVE = "min-h-[848px] sm:min-h-[640px]";

/** The real form's input ids (no heading prop, so BirthDetailsForm's "-default" ids). */
export const CAREER_CHART_CHECK_FIELD_IDS = {
  date: "birthDate-default",
  time: "birthTime-default",
  place: "birthPlace-default",
} as const;

/** free_tool_started and calculator_used placement for the /career-horoscope/ check. */
export const CAREER_CHART_CHECK_PLACEMENT = "career_horoscope_page_check";
